/**
 * JKD FAQ integration bundle entry.
 *
 * - Imports the SCSS so Vite emits faq.min.css.
 * - Filter pills: instant client-side toggle by `data-faq-cat`.
 * - Accordion: native <details>/<summary>; this only groups the rows so
 *   opening one closes the others.
 *
 * Self-contained: mounts only when a `[data-faq-root]` element is present,
 * so it is a no-op on every other route. No theme or Pro dependency.
 */

import '../scss/faq.scss';

const SELECTORS = {
	root: '[data-faq-root]',
	filterPill: '[data-faq-cat]',
	item: '[data-faq-item]',
	details: '.jkd-faq__details',
	// Shared `.jkd-filters-list` show-all contract.
	showAll: '[data-jkd-filters-show-all]',
	filterHidden: '[data-jkd-filter-hidden]',
};

const applyFilter = (pills, items, slug) => {
	pills.forEach((pill) => {
		const isActive = pill.dataset.faqCat === slug;
		// Shared chip contract: active = solid `.jkd-label`, inactive = secondary.
		pill.classList.toggle('jkd-label--secondary', !isActive);
		pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
	});

	if (slug === '*') {
		items.forEach((item) => item.removeAttribute('hidden'));
		return;
	}

	items.forEach((item) => {
		const cats = (item.dataset.faqCats || '').split(/\s+/).filter(Boolean);
		if (cats.includes(slug)) {
			item.removeAttribute('hidden');
		} else {
			item.setAttribute('hidden', '');
		}
	});
};

const handleFilter = (root) => {
	const pills = Array.from(root.querySelectorAll(SELECTORS.filterPill));
	if (pills.length === 0) return;

	const items = Array.from(root.querySelectorAll(SELECTORS.item));

	pills.forEach((pill) => {
		pill.addEventListener('click', (e) => {
			const slug = pill.dataset.faqCat;
			if (!slug) return;
			e.preventDefault();
			applyFilter(pills, items, slug);
		});
	});

	// Sync the initial view to whichever pill is active on load — the "All" pill
	// when present, otherwise the first category. Items now ship visible, so this
	// is what narrows them to the active scope.
	const activePill =
		pills.find((pill) => pill.getAttribute('aria-pressed') === 'true') || pills[0];
	if (activePill && activePill.dataset.faqCat) {
		applyFilter(pills, items, activePill.dataset.faqCat);
	}
};

const handleShowAll = (root) => {
	const btn = root.querySelector(SELECTORS.showAll);
	if (!btn) return;

	btn.addEventListener('click', (e) => {
		e.preventDefault();
		Array.from(root.querySelectorAll(SELECTORS.filterHidden)).forEach((pill) => {
			pill.hidden = false;
			pill.removeAttribute('data-jkd-filter-hidden');
			pill.classList.remove('jkd-filters-list__item--hidden');
		});
		btn.remove();
	});
};

// Accordion behaviour: opening one answer closes whichever was open before.
//
// A shared `name` makes the rows a native exclusive group: the browser closes
// the old row in the same frame it opens the new one, so both height transitions
// start together and a list of equal answers keeps its height. Closing from a
// `toggle` listener started the close a frame late (the event is queued), and the
// page jumped by that frame's worth of height.
let groupIndex = 0;
const handleCloseOthers = (root) => {
	const name = `jkd-faq-${++groupIndex}`;
	root.querySelectorAll(SELECTORS.details).forEach((el) => {
		el.name = name;
	});
};

// A page can hold more than one listing (block, shortcode, widget), so every
// root is wired, not just the first one.
const boot = () => {
	document.querySelectorAll(SELECTORS.root).forEach((root) => {
		handleFilter(root);
		handleShowAll(root);
		handleCloseOthers(root);
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
	boot();
}
